/** @format */

import i18n from '../core/i18n';
import { DiscountInterface } from '../interfaces/billing/DiscountInterface';
import { DiscountEntity } from '../views/Discount/PlatformResellerDiscount/PlatformResellerDiscount';
import { DiscountRateEntity } from '../views/Discount/PlatformResellerDiscount/PlatformResellerRateDiscount';

export class DiscountHelper {
    static TYPES = {
        TYPE_FREE_MINUTES_GLOBAL: 0,
        TYPE_FREE_MINUTES_PER_FRACTION: 1,
        TYPE_FREE_MINUTES_PER_PREFIX: 2,
        TYPE_MINIMAL_PAYMENT_GLOBAL: 3,
        TYPE_MINIMAL_PAYMENT_PER_FRACTION: 4,
        TYPE_MINIMAL_PAYMENT_PER_PREFIX: 5,
        TYPE_FREE_USAGE_PER_PREFIX: 6,
        TYPE_FREE_USAGE_PER_PREFIX_FIRST_ONLY: 7,
        TYPE_PROPORTIONATE_GLOBAL: 8,
        TYPE_PROPORTIONATE_PER_FRACTION: 9,
        TYPE_PROPORTIONATE_PER_PREFIX: 10,
        TYPE_RATES_GLOBAL: 11,
        TYPE_RATES_FRACTION: 12,
        TYPE_RATES_PREFIX: 13,
        TYPE_FREE_USAGE_PER_FRACTION: 14,
        TYPE_FREE_USAGE_PER_FRACTION_FIRST_ONLY: 15,
    };

    static DESTINATION = {
        DESTINATION_ANYTHING: 1,
        DESTINATION_FRACTIONS: 2,
        DESTINATION_PREFIXES: 3,
    };

    static DAYS = {
        DAYS_ALL: -2,
        DAYS_HOLIDAY: 8,
        DAYS_WORKDAY: -1,
        DAYS_SUNDAY: 1,
        DAYS_MONDAY: 2,
        DAYS_TUESDAY: 3,
        DAYS_WEDNESDAY: 4,
        DAYS_THURSDAY: 5,
        DAYS_FRIDAY: 6,
        DAYS_SATURDAY: 7,
    };

    static DAYS_SELECT = {
        ALL_DAYS: 0,
        WORKDAY_HOLIDAY: 1,
        ALL_DAYS_HOLIDAYS: 2,
    };

    static FLAGS = {
        FLAGS_SKIP_INITIAL: 1,
        FLAGS_BLOCK_OTHERS: 2,
    };

    static DISCOUNTS = {
        DISCOUNT_TEMPLATE_RATE_CUSTOM: 1,
        DISCOUNT_TEMPLATE_RATE_PAYMENT: 2,
    };

    static COST_TYPE = {
        CALL: 4,
        SERVICE: 5,
    };

    static VALUE_TYPE = {
        TYPE_FREE_MINUTES: 0,
        TYPE_FREE_USAGE: 1,
        TYPE_MINIMAL_PAYMENT: 2,
        TYPE_PROPORTIONATE: 3,
    };

    static DISCOUNT_PRODUCT_OPERATIONS = {
        DISCOUNT_PRODUCT_OPERATION_SET_END: 1,
        DISCOUNT_PRODUCT_OPERATION_CANCEL_SET_END: 2,
    };

    static PARAM_PROPERTY = {
        PRICE_THRESHOLD: 'price_threshold',
    };

    static getTypesForDiscount = (): Array<{ name: string; value: number }> => {
        return [
            { name: 'discount:types.type_free_minutes_global', value: DiscountHelper.TYPES.TYPE_FREE_MINUTES_GLOBAL },
            { name: 'discount:types.type_free_minutes_per_fraction', value: DiscountHelper.TYPES.TYPE_FREE_MINUTES_PER_FRACTION },
            { name: 'discount:types.type_free_minutes_per_prefix', value: DiscountHelper.TYPES.TYPE_FREE_MINUTES_PER_PREFIX },
            { name: 'discount:types.type_minimal_payment_global', value: DiscountHelper.TYPES.TYPE_MINIMAL_PAYMENT_GLOBAL },
            { name: 'discount:types.type_minimal_payment_per_fraction', value: DiscountHelper.TYPES.TYPE_MINIMAL_PAYMENT_PER_FRACTION },
            { name: 'discount:types.type_minimal_payment_per_prefix', value: DiscountHelper.TYPES.TYPE_MINIMAL_PAYMENT_PER_PREFIX },
            { name: 'discount:types.type_free_usage_per_prefix', value: DiscountHelper.TYPES.TYPE_FREE_USAGE_PER_PREFIX },
            { name: 'discount:types.type_free_usage_per_prefix_first_only', value: DiscountHelper.TYPES.TYPE_FREE_USAGE_PER_PREFIX_FIRST_ONLY },
            { name: 'discount:types.type_proportionate_global', value: DiscountHelper.TYPES.TYPE_PROPORTIONATE_GLOBAL },
            { name: 'discount:types.type_proportionate_per_fraction', value: DiscountHelper.TYPES.TYPE_PROPORTIONATE_PER_FRACTION },
            { name: 'discount:types.type_proportionate_per_prefix', value: DiscountHelper.TYPES.TYPE_PROPORTIONATE_PER_PREFIX },
            { name: 'discount:types.type_free_usage_per_fraction', value: DiscountHelper.TYPES.TYPE_FREE_USAGE_PER_FRACTION },
            { name: 'discount:types.type_free_usage_per_fraction_first_only', value: DiscountHelper.TYPES.TYPE_FREE_USAGE_PER_FRACTION_FIRST_ONLY },
        ];
    };

    static matchTypes = (type: number): string => {
        let types = DiscountHelper.getTypesForDiscount();
        let match = '';
        types.forEach((el: { name: string; value: number }) => {
            if (el.value === type) {
                match = el.name;
            }
        });

        if (match.length === 0) {
            types = DiscountHelper.getTypesForRateDiscount();
            types.forEach((el: { name: string; value: number }) => {
                if (el.value === type) {
                    match = el.name;
                }
            });
        }

        return match;
    };

    static getTypesForRateDiscount = (): Array<{ name: string; value: number }> => {
        return [
            { name: 'discount:s.fractions', value: DiscountHelper.TYPES.TYPE_RATES_FRACTION },
            { name: 'discount:s.prefixes', value: DiscountHelper.TYPES.TYPE_RATES_PREFIX },
        ];
    };

    static getType = (): Array<{ name: string; value: number }> => {
        return [
            { name: i18n.t('discount:s.call'), value: DiscountHelper.COST_TYPE.CALL },
            { name: i18n.t('discount:s.service'), value: DiscountHelper.COST_TYPE.SERVICE },
        ];
    };

    static getDestinationType = (): Array<{ name: string; value: number }> => {
        return [
            { name: i18n.t('discount:s.anything'), value: DiscountHelper.DESTINATION.DESTINATION_ANYTHING },
            { name: i18n.t('discount:s.fractions'), value: DiscountHelper.DESTINATION.DESTINATION_FRACTIONS },
            { name: i18n.t('discount:s.prefixes'), value: DiscountHelper.DESTINATION.DESTINATION_PREFIXES },
        ];
    };

    static getRateDestinationType = (): Array<{ name: string; value: number }> => {
        return [
            { name: i18n.t('discount:s.fractions'), value: DiscountHelper.DESTINATION.DESTINATION_FRACTIONS },
            { name: i18n.t('discount:s.prefixes'), value: DiscountHelper.DESTINATION.DESTINATION_PREFIXES },
        ];
    };

    static getValueType = (): Array<{ name: string; value: number }> => {
        return [
            { name: i18n.t('discount:s.free_minutes'), value: DiscountHelper.VALUE_TYPE.TYPE_FREE_MINUTES },
            { name: i18n.t('discount:s.free_usage'), value: DiscountHelper.VALUE_TYPE.TYPE_FREE_USAGE },
            { name: i18n.t('discount:s.minimal_payment'), value: DiscountHelper.VALUE_TYPE.TYPE_MINIMAL_PAYMENT },
            { name: i18n.t('discount:s.proportionate'), value: DiscountHelper.VALUE_TYPE.TYPE_PROPORTIONATE },
        ];
    };

    static getLayout = (): Array<{ name: string; value: number }> => {
        return [
            { name: i18n.t('discount:s.days'), value: DiscountHelper.DAYS_SELECT.ALL_DAYS },
            // { name: i18n.t('discount:s.holiday'), value: DiscountHelper.DAYS_SELECT.WORKDAY_HOLIDAY },
            // { name: i18n.t('discount:s.workday'), value: DiscountHelper.DAYS_SELECT.ALL_DAYS_HOLIDAYS },
        ];
    };

    static prepareFlagProperty = (onlyThisPromo: boolean, skipInitialPrice: boolean, type: number): number => {
        const fixedSkipInitialPrice = type === DiscountHelper.VALUE_TYPE.TYPE_FREE_MINUTES ? skipInitialPrice : false;
        if (!onlyThisPromo && !fixedSkipInitialPrice) {
            return 0;
        } else if (!onlyThisPromo && fixedSkipInitialPrice) {
            return 1;
        } else if (onlyThisPromo && !fixedSkipInitialPrice) {
            return 2;
        } else {
            return 3;
        }
    };

    static getValueForFreeMinutes = (filterType: number): number => {
        switch (filterType) {
            case DiscountHelper.DESTINATION.DESTINATION_ANYTHING:
                return DiscountHelper.TYPES.TYPE_FREE_MINUTES_GLOBAL;
            case DiscountHelper.DESTINATION.DESTINATION_FRACTIONS:
                return DiscountHelper.TYPES.TYPE_FREE_MINUTES_PER_FRACTION;
            case DiscountHelper.DESTINATION.DESTINATION_PREFIXES:
                return DiscountHelper.TYPES.TYPE_FREE_MINUTES_PER_PREFIX;
            default:
                return -1;
        }
    };

    static getValueForFreeUsage = (filterType: number, firstMonthOnly: boolean): number => {
        switch (filterType) {
            case DiscountHelper.DESTINATION.DESTINATION_ANYTHING:
                return -1;
            case DiscountHelper.DESTINATION.DESTINATION_FRACTIONS:
                if (firstMonthOnly) {
                    return DiscountHelper.TYPES.TYPE_FREE_USAGE_PER_FRACTION_FIRST_ONLY;
                } else {
                    return DiscountHelper.TYPES.TYPE_FREE_USAGE_PER_FRACTION;
                }
            case DiscountHelper.DESTINATION.DESTINATION_PREFIXES:
                if (firstMonthOnly) {
                    return DiscountHelper.TYPES.TYPE_FREE_USAGE_PER_PREFIX_FIRST_ONLY;
                } else {
                    return DiscountHelper.TYPES.TYPE_FREE_USAGE_PER_PREFIX;
                }
            default:
                return -1;
        }
    };

    static getValueForMinimalPayment = (filterType: number): number => {
        switch (filterType) {
            case DiscountHelper.DESTINATION.DESTINATION_ANYTHING:
                return DiscountHelper.TYPES.TYPE_MINIMAL_PAYMENT_GLOBAL;
            case DiscountHelper.DESTINATION.DESTINATION_FRACTIONS:
                return DiscountHelper.TYPES.TYPE_MINIMAL_PAYMENT_PER_FRACTION;
            case DiscountHelper.DESTINATION.DESTINATION_PREFIXES:
                return DiscountHelper.TYPES.TYPE_MINIMAL_PAYMENT_PER_PREFIX;
            default:
                return -1;
        }
    };

    static getValueForProportionate = (filterType: number): number => {
        switch (filterType) {
            case DiscountHelper.DESTINATION.DESTINATION_ANYTHING:
                return DiscountHelper.TYPES.TYPE_PROPORTIONATE_GLOBAL;
            case DiscountHelper.DESTINATION.DESTINATION_FRACTIONS:
                return DiscountHelper.TYPES.TYPE_PROPORTIONATE_PER_FRACTION;
            case DiscountHelper.DESTINATION.DESTINATION_PREFIXES:
                return DiscountHelper.TYPES.TYPE_PROPORTIONATE_PER_PREFIX;
            default:
                return -1;
        }
    };

    static getValueForRate = (filterType: number): number => {
        if (filterType === DiscountHelper.DESTINATION.DESTINATION_PREFIXES) {
            return DiscountHelper.TYPES.TYPE_RATES_PREFIX;
        }

        if (filterType === DiscountHelper.DESTINATION.DESTINATION_FRACTIONS) {
            return DiscountHelper.TYPES.TYPE_RATES_FRACTION;
        }

        return -1;
    };

    static prepareTypeProperty = (type: number, filterType: number, firstMonthOnly: boolean): number => {
        switch (type) {
            case DiscountHelper.VALUE_TYPE.TYPE_FREE_MINUTES:
                return DiscountHelper.getValueForFreeMinutes(filterType);
            case DiscountHelper.VALUE_TYPE.TYPE_FREE_USAGE:
                return DiscountHelper.getValueForFreeUsage(filterType, firstMonthOnly);
            case DiscountHelper.VALUE_TYPE.TYPE_MINIMAL_PAYMENT:
                return DiscountHelper.getValueForMinimalPayment(filterType);
            case DiscountHelper.VALUE_TYPE.TYPE_PROPORTIONATE:
                return DiscountHelper.getValueForProportionate(filterType);
            case 4:
                return DiscountHelper.getValueForRate(filterType);
            case 5:
                return DiscountHelper.getValueForRate(filterType);
            default:
                return -1;
        }
    };

    static checkProperValueOfOneTimeProperty = (oneTime: boolean, destination: number, firstMonthOnly: boolean): boolean => {
        if (destination === DiscountHelper.VALUE_TYPE.TYPE_FREE_USAGE && firstMonthOnly) {
            return false;
        } else {
            return oneTime;
        }
    };

    static prepareDestination = (
        destination: string | null | number | Array<{ prefix: string; mainname: string; id: number }>,
        filterType: number
    ): string | null | number => {
        let finalValue: Array<number | string> = [];
        if (Array.isArray(destination)) {
            if (filterType === DiscountHelper.DESTINATION.DESTINATION_FRACTIONS) {
                destination.forEach((el) => {
                    finalValue.push(el.id);
                });
            }

            if (filterType === DiscountHelper.DESTINATION.DESTINATION_PREFIXES) {
                destination.forEach((el) => {
                    finalValue.push(el.prefix);
                });
            }

            return finalValue.join(',');
        } else {
            return destination;
        }

        // if(filterType === DiscountHelper.DESTINATION.DESTINATION_PREFIXES) {
        //     if (Array.isArray(destination)) {
        //         destination.forEach((el) => {
        //             finalValue.push(el.);
        //         });
        //
        //         return finalValue.join(',');
        //     } else {
        //         return destination;
        //     }
        // }
    };

    static prepareFinalDiscountWithParams = (discountEntity: DiscountEntity | DiscountRateEntity): any => {
        let finalDiscountParam = {
            flags: DiscountHelper.prepareFlagProperty(discountEntity.onlyThisPromo, discountEntity.skipInitialPrice, discountEntity.type),
            historyLength: discountEntity.historyLength,
            oneTime: DiscountHelper.checkProperValueOfOneTimeProperty(discountEntity.oneTime, discountEntity.type, discountEntity.firstMonthOnly),
            count:
                discountEntity.type === DiscountHelper.VALUE_TYPE.TYPE_PROPORTIONATE && discountEntity.count
                    ? discountEntity.count * 100
                    : discountEntity.count,
            destination: DiscountHelper.prepareDestination(discountEntity.destination, discountEntity.filterType),
            linearBillingOverride: null, // Uzupełnianie po stronie base-api
            periods: null, // Do ogarnięcia metoda tworząca na podstawie podanych danych,
            bond:
                              DiscountHelper.prepareTypeProperty(discountEntity.type, discountEntity.filterType, discountEntity.firstMonthOnly) ===
                    DiscountHelper.TYPES.TYPE_MINIMAL_PAYMENT_PER_FRACTION ||
                DiscountHelper.prepareTypeProperty(discountEntity.type, discountEntity.filterType, discountEntity.firstMonthOnly) ===
                    DiscountHelper.TYPES.TYPE_MINIMAL_PAYMENT_PER_PREFIX ||
                DiscountHelper.prepareTypeProperty(discountEntity.type, discountEntity.filterType, discountEntity.firstMonthOnly) ===
                    DiscountHelper.TYPES.TYPE_MINIMAL_PAYMENT_GLOBAL
                    ? discountEntity.count
                    : 0, // Do ogarnięcia jak uzupełnić
            discountId:       null, // Do uzupełnienia po stronie base-api
            propOnFirstMonth: !(discountEntity.propOnFirstMonth === false || discountEntity.propOnFirstMonth === null),
        };

        // const prId = discountEntity.platformResellerId ? discountEntity.platformResellerId : null;

        let finalDiscount = {
            platformResellerId: discountEntity.platformResellerId,
            mainname: discountEntity.name,
            discountParam: JSON.stringify(finalDiscountParam),
            historyLength: discountEntity.historyLength,
            autoShare: discountEntity.autoShare,
            dateStart: discountEntity.dateStart,
            dateEnd: discountEntity.dateEnd,
            type: DiscountHelper.prepareTypeProperty(discountEntity.type, discountEntity.filterType, discountEntity.firstMonthOnly),
            gracePeriod: discountEntity.gracePeriod,
            payment: discountEntity.payment,
            active: discountEntity.active,
            priority: typeof discountEntity.priority === 'string' ? parseInt(discountEntity.priority) : discountEntity.priority,
            customerId: -1, // Uzupełnianie po stronie base-api
            monthMask: null, //
            clientCustomers: {},
            resellerCustomers: {},
            otherCustomers: null,
            discountTemplateId: null, // Na razie null raczej uzupełnić dopiero jak będę dodawać szablony,
            discountTemplateRate: null, // Na razie null raczej uzupełnić dopiero jak będę dodawać szablony,
            discountServiceId: null, // Na razie null raczej uzupełnić dopiero jak będę dodawać szablony,
            rateParam: discountEntity.rateParam ?? null,
            params: discountEntity.params ?? null,
            allowUseOnProduct: discountEntity.allowUseOnProduct,
            customPrefix: discountEntity.customPrefix,
            useAllResellerNumbers: discountEntity.useAllResellerNumbers,
        };

        return finalDiscount;
    };

    static validateDiscountEntityBeforePersist = (discountEntity: DiscountEntity): Array<{ key: string; value: string }> => {
        const errors = [];

        if (!discountEntity.name) {
            errors.push({ key: 'name', value: 'discount:n.name' });
        }

        if (discountEntity.type === null) {
            errors.push({ key: 'type', value: 'discount:n.type' });
        }
        if (discountEntity.count === null) {
            errors.push({ key: 'count', value: 'discount:n.count' });
        }

        if (discountEntity.historyLength === null) {
            errors.push({ key: 'historyLength', value: 'discount:n.historyLength' });
        }

        if (!discountEntity.dateStart) {
            errors.push({ key: 'dateStart', value: 'discount:n.dateStart' });
        }

        if (
            discountEntity.filterType !== DiscountHelper.DESTINATION.DESTINATION_ANYTHING &&
            (discountEntity.destination === null || discountEntity.destination.length === 0)
        ) {
            errors.push({ key: 'filterType', value: 'discount:n.filterType' });
        }

        return errors;
    };
    static validateDiscountRateEntityBeforePersist = (discountRateEntity: DiscountRateEntity): Array<{ key: string; value: string }> => {
        const errors = [];

        if (!discountRateEntity.name) {
            errors.push({ key: 'name', value: 'discount:n.name' });
        }

        if (discountRateEntity.type === null) {
            errors.push({ key: 'type', value: 'discount:n.type' });
        }

        if (discountRateEntity.layout === null) {
            errors.push({ key: 'layout', value: 'discount:n.layout' });
        }

        if (discountRateEntity.historyLength === null) {
            errors.push({ key: 'historyLength', value: 'discount:n.historyLength' });
        }

        if (!discountRateEntity.dateStart) {
            errors.push({ key: 'dateStart', value: 'discount:n.dateStart' });
        }

        if (
            discountRateEntity.filterType !== DiscountHelper.DESTINATION.DESTINATION_ANYTHING &&
            (discountRateEntity.destination === null || discountRateEntity.destination.length === 0)
        ) {
            errors.push({ key: 'filterType', value: 'discount:n.filterType' });
        }

        return errors;
    };

    static getFlagFromDiscount = (discount: DiscountInterface): { onlyThisPromo: boolean; skipInitialPrice: boolean } => {
        let dp = {
            onlyThisPromo: false,
            skipInitialPrice: false,
        };
        if (typeof discount.discountParam === 'string') {
            const discountParam = JSON.parse(discount.discountParam);

            switch (discountParam.flags) {
                case 0:
                    break;
                case 1:
                    dp.skipInitialPrice = true;
                    break;
                case 2:
                    dp.onlyThisPromo = true;
                    break;
                case 3:
                    dp.onlyThisPromo = true;
                    dp.skipInitialPrice = true;
                    break;
                default:
                    return dp;
            }
        }

        return dp;
    };

    // static getFromOneTime = (discount: DiscountInterface) => {
    //     switch (discount.oneTime) {
    //
    //     }
    // }

    static getFromType = (discount: DiscountInterface): { type: number; filterType: number; firstMonthOnly: boolean } => {
        let x = {
            type: 0,
            filterType: 0,
            firstMonthOnly: false,
        };
        switch (discount.type) {
            case DiscountHelper.TYPES.TYPE_FREE_MINUTES_GLOBAL:
                x.type = DiscountHelper.VALUE_TYPE.TYPE_FREE_MINUTES;
                x.filterType = DiscountHelper.DESTINATION.DESTINATION_ANYTHING;
                break;
            case DiscountHelper.TYPES.TYPE_FREE_MINUTES_PER_FRACTION:
                x.type = DiscountHelper.VALUE_TYPE.TYPE_FREE_MINUTES;
                x.filterType = DiscountHelper.DESTINATION.DESTINATION_FRACTIONS;
                break;
            case DiscountHelper.TYPES.TYPE_FREE_MINUTES_PER_PREFIX:
                x.type = DiscountHelper.VALUE_TYPE.TYPE_FREE_MINUTES;
                x.filterType = DiscountHelper.DESTINATION.DESTINATION_PREFIXES;
                break;
            case DiscountHelper.TYPES.TYPE_FREE_USAGE_PER_FRACTION:
                x.type = DiscountHelper.VALUE_TYPE.TYPE_FREE_USAGE;
                x.filterType = DiscountHelper.DESTINATION.DESTINATION_FRACTIONS;
                break;
            case DiscountHelper.TYPES.TYPE_FREE_USAGE_PER_PREFIX:
                x.type = DiscountHelper.VALUE_TYPE.TYPE_FREE_USAGE;
                x.filterType = DiscountHelper.DESTINATION.DESTINATION_PREFIXES;
                break;
            case DiscountHelper.TYPES.TYPE_FREE_USAGE_PER_FRACTION_FIRST_ONLY:
                x.type = DiscountHelper.VALUE_TYPE.TYPE_FREE_USAGE;
                x.filterType = DiscountHelper.DESTINATION.DESTINATION_FRACTIONS;
                x.firstMonthOnly = true;
                break;
            case DiscountHelper.TYPES.TYPE_FREE_USAGE_PER_PREFIX_FIRST_ONLY:
                x.type = DiscountHelper.VALUE_TYPE.TYPE_FREE_USAGE;
                x.filterType = DiscountHelper.DESTINATION.DESTINATION_PREFIXES;
                x.firstMonthOnly = true;
                break;
            case DiscountHelper.TYPES.TYPE_MINIMAL_PAYMENT_GLOBAL:
                x.type = DiscountHelper.VALUE_TYPE.TYPE_MINIMAL_PAYMENT;
                x.filterType = DiscountHelper.DESTINATION.DESTINATION_ANYTHING;
                break;
            case DiscountHelper.TYPES.TYPE_MINIMAL_PAYMENT_PER_FRACTION:
                x.type = DiscountHelper.VALUE_TYPE.TYPE_MINIMAL_PAYMENT;
                x.filterType = DiscountHelper.DESTINATION.DESTINATION_FRACTIONS;
                break;
            case DiscountHelper.TYPES.TYPE_MINIMAL_PAYMENT_PER_PREFIX:
                x.type = DiscountHelper.VALUE_TYPE.TYPE_MINIMAL_PAYMENT;
                x.filterType = DiscountHelper.DESTINATION.DESTINATION_PREFIXES;
                break;
            case DiscountHelper.TYPES.TYPE_PROPORTIONATE_GLOBAL:
                x.type = DiscountHelper.VALUE_TYPE.TYPE_PROPORTIONATE;
                x.filterType = DiscountHelper.DESTINATION.DESTINATION_ANYTHING;
                break;
            case DiscountHelper.TYPES.TYPE_PROPORTIONATE_PER_FRACTION:
                x.type = DiscountHelper.VALUE_TYPE.TYPE_PROPORTIONATE;
                x.filterType = DiscountHelper.DESTINATION.DESTINATION_FRACTIONS;
                break;
            case DiscountHelper.TYPES.TYPE_PROPORTIONATE_PER_PREFIX:
                x.type = DiscountHelper.VALUE_TYPE.TYPE_PROPORTIONATE;
                x.filterType = DiscountHelper.DESTINATION.DESTINATION_PREFIXES;
                break;
            case DiscountHelper.TYPES.TYPE_RATES_FRACTION:
                x.type = DiscountHelper.COST_TYPE.CALL;
                x.filterType = DiscountHelper.DESTINATION.DESTINATION_FRACTIONS;
                break;
            case DiscountHelper.TYPES.TYPE_RATES_PREFIX:
                x.type = DiscountHelper.COST_TYPE.CALL;
                x.filterType = DiscountHelper.DESTINATION.DESTINATION_PREFIXES;
                break;
            default:
                return x;
        }

        return x;
    };

    static setEntityValueFromDB = (discount: DiscountInterface, platformResellerId: number): any => {
        let discountParam = null;
        if (typeof discount.discountParam === 'string') {
            discountParam = JSON.parse(discount.discountParam);
        }

        const { onlyThisPromo, skipInitialPrice } = DiscountHelper.getFlagFromDiscount(discount);
        const { type, filterType, firstMonthOnly } = DiscountHelper.getFromType(discount);

        const discountInitial = {
            customerId: discount.customerId,
            name: discount.mainname,
            active: discount.active,
            onlyThisPromo: onlyThisPromo,
            type: type,
            count: type === DiscountHelper.VALUE_TYPE.TYPE_PROPORTIONATE ? discountParam.count / 100 : discount.count,
            historyLength: discountParam.historyLength ?? 0,
            dateStart: discount.dateStart,
            dateEnd: discount.dateEnd,
            gracePeriod: discount.gracePeriod,
            payment: !!discount.payment,
            skipInitialPrice: skipInitialPrice,
            firstMonthOnly: firstMonthOnly,
            filterType: filterType,
            autoShare: !!discount.autoShare,
            platformResellerId: platformResellerId,
            oneTime: discountParam.oneTime,
            destination: discount.elements && typeof discount.elements === 'string' ? JSON.parse(discount.elements) : discountParam.destination,
            rateParam: discount.rateParam ? JSON.parse(discount.rateParam)[0] : null,
            layout: 0,
            currency: 0,
            allowUseOnProduct: discount.allowUseOnProduct,
            customPrefix: discount.customPrefix,
            useAllResellerNumbers: discount.useAllResellerNumbers,
            priority: discount.priority,
            propOnFirstMonth: discountParam.propOnFirstMonth,
        };

        return discountInitial;
    };
}
